import { apiClient } from '../client';
import { API_ENDPOINTS, API_CONFIG } from '../config';
import { tokenRefreshService } from '../../../services/TokenRefreshService';

export interface ProfileResult {
  data: User;
  error: string | null;
  sessionRejected: boolean;
}

export interface User {
  id: string;
  full_name: string;
  email?: string | null; // null for phone-first accounts (no email)
  phone?: string;
  company?: string;
  role: string;
  avatar_url?: string;
  timezone?: string;
  preferences?: any;
  is_organization_owner?: boolean;
  is_super_admin?: boolean;
  is_agency?: boolean;
  organization_id?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  totp_enabled?: boolean;
  has_password?: boolean; // false for phone-first accounts until they set one
}

export interface LoginCredentials {
  email: string;
  password: string;
  adminLogin?: boolean; // Flag to indicate super admin login
}

export interface SignupData {
  full_name: string;
  email: string;
  password: string;
  organization_name?: string;
  organization_type?: 'business' | 'nonprofit' | 'government' | 'education' | 'healthcare' | 'other';
  organization_description?: string;
  role?: 'ADMIN' | 'USER' | 'AGENT';
  agency_referral_code?: string; // Optional referral code from agency
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken?: string;
  requires_totp?: boolean;
  userId?: string;
}

export interface PasswordResetData {
  email: string;
}

export interface PasswordResetConfirmData {
  token: string;
  password: string;
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<{ data: AuthResponse; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return { data: null as any, error: errorData.error || errorData.message || 'Login failed' };
      }

      const data = await response.json();
      return { data: data.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async signup(userData: SignupData): Promise<{ data: AuthResponse; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/simple-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Registration failed' };
      }

      const data = await response.json();
      return { data: data.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async signupWithPhone(data: { full_name?: string; organization_name?: string; phoneNumber: string; otpCode: string; agency_referral_code?: string }): Promise<{ data: AuthResponse; error: string | null; errorCode?: string }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/register-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Registration failed',
          errorCode: errorData.errorCode,
        };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async loginWithOtp(phoneNumber: string, otpCode: string, adminLogin: boolean = false): Promise<{ data: AuthResponse; error: string | null; errorCode?: string }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/login-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber, otpCode, adminLogin }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Login failed',
          errorCode: errorData.errorCode,
        };
      }

      const data = await response.json();
      return { data: data.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async getProfile(): Promise<ProfileResult> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return { data: null as any, error: 'No authentication token', sessionRejected: true };
      }

      const requestId =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `profile-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 401) {
          const outcome = await tokenRefreshService.forceRefresh();

          if (outcome === 'refreshed') {
            const retryToken = localStorage.getItem('auth_token');
            const retry = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/profile`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${retryToken}`,
                'Content-Type': 'application/json',
                'X-Request-Id': requestId,
              },
            });
            if (retry.ok) {
              const retried = await retry.json();
              return { data: retried.data, error: null, sessionRejected: false };
            }
            return {
              data: null as any,
              error: 'Failed to get profile',
              sessionRejected: retry.status === 401,
            };
          }

          if (outcome === 'unavailable') {
            console.warn(
              '[authApi.getProfile] 401 then an unverifiable refresh — keeping the session. This is a backend/network problem, not a rejection.',
              { requestId, code: errorData.code }
            );
            return {
              data: null as any,
              error: "We're having a temporary server problem. Please try again in a moment.",
              sessionRejected: false,
            };
          }

          console.error(
            '[authApi.getProfile] 401 and the refresh was REJECTED — the session is genuinely invalid. Search backend logs for this requestId.',
            { requestId, code: errorData.code, error: errorData.error || errorData.message }
          );
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          return { data: null as any, error: 'Token expired', sessionRejected: true };
        }

        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Failed to get profile',
          sessionRejected: false,
        };
      }

      const data = await response.json();
      return { data: data.data, error: null, sessionRejected: false };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error', sessionRejected: false };
    }
  },

  async refreshToken(): Promise<{ data: { token: string }; error: string | null }> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return { data: null as any, error: 'No refresh token' };
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Token refresh failed' };
      }

      const data = await response.json();
      return { data: data.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async logout(): Promise<{ data: null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }

      localStorage.clear();

      return { data: null, error: null };
    } catch (error: any) {
      localStorage.clear();
      return { data: null, error: null };
    }
  },

  async requestPasswordReset(data: PasswordResetData): Promise<{ data: { message: string }; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Password reset request failed'
        };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async resetPassword(data: PasswordResetConfirmData): Promise<{ data: { message: string }; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Password reset failed'
        };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async verifyEmail(token: string): Promise<{ data: { message: string }; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Email verification failed' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async sendOTP(phoneNumber: string, name?: string, email?: string, purpose: 'signup' | 'login' | 'auto' = 'signup'): Promise<{ data: { phoneNumber?: string; email?: string; expiresIn: number; sentTo: string[]; user_exists?: boolean }; error: string | null; errorCode?: string }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber, name, email, purpose }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'Failed to send OTP',
          errorCode: errorData.errorCode,
        };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async verifyOTP(emailOrPhone: string, otpCode: string): Promise<{ data: { email?: string; phoneNumber?: string; verified: boolean; verifiedMethods?: string[] }; error: string | null; errorCode?: string }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailOrPhone, otpCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null as any,
          error: errorData.error || errorData.message || 'OTP verification failed',
          errorCode: errorData.errorCode,
        };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async setupTotp(): Promise<{ data: { secret: string; dataUrl: string }; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return { data: null as any, error: 'No authentication token' };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/totp/setup`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Failed to setup TOTP' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async enableTotp(token: string): Promise<{ data: { backupCodes: string[] }; error: string | null }> {
    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) return { data: null as any, error: 'No authentication token' };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/totp/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Failed to enable TOTP' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async disableTotp(password: string): Promise<{ data: { message: string }; error: string | null }> {
    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) return { data: null as any, error: 'No authentication token' };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/totp/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'Failed to disable TOTP' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },

  async verifyLoginTotp(userId: string, token: string): Promise<{ data: AuthResponse; error: string | null }> {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/login-totp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null as any, error: errorData.error || errorData.message || 'TOTP verification failed' };
      }

      const data = await response.json();
      return { data: data.data, error: null };
    } catch (error: any) {
      return { data: null as any, error: error.message || 'Network error' };
    }
  },
};
